/**
 * Edit / enrich a project (T036, US3 — FR-007 / SC-004). Loads a project, applies additive edits
 * (add a repo, mark closed, change status) via the existing upsert callables, and shows its audit
 * trail with recoverable prior state. Editing the delivery/ops area requires pm/admin (server-enforced).
 */
import { useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { SessionUser } from '../services/auth';
import type { Project, ProjectStatus } from '../types/entities';
import { upsertProject, upsertSourceLink } from '../services/onboarding';
import { AuditTrail } from '../components/AuditTrail';
import { ChannelMapper } from '../components/ChannelMapper';

export function EditProject({ user }: { user: SessionUser }) {
  const canEdit = user.role === 'pm' || user.role === 'admin';

  const [projectId, setProjectId] = useState('');
  const [project, setProject] = useState<Project | null>(null);
  const [repoUrl, setRepoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setError(null);
    setNote(null);
    try {
      const snap = await getDoc(doc(db, 'projects', projectId.trim()));
      if (!snap.exists()) {
        setProject(null);
        setError(`Project ${projectId} not found.`);
        return;
      }
      setProject(snap.data() as Project);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    }
  };

  const afterWrite = (msg: string) => {
    setNote(msg);
    setRefreshKey((k) => k + 1); // reload the audit trail
    void load(); // refresh the current doc
  };

  const addRepo = async () => {
    if (!project || !repoUrl.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await upsertSourceLink({
        project_id: project.project_id,
        kind: 'github_repo',
        external_id: repoUrl.trim(),
      });
      setRepoUrl('');
      afterWrite(`Repo added (event ${res.event_id}).`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add repo failed');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: ProjectStatus) => {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const res = await upsertProject({
        project_id: project.project_id,
        client_id: project.client_id,
        name: project.name,
        pm_tool: project.pm_tool,
        type: project.type,
        status,
      });
      afterWrite(`Status → ${status} (event ${res.event_id}).`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h1>Edit project</h1>
      {!canEdit && (
        <p style={banner}>Editing the delivery area requires the pm or admin role — read-only (SC-006).</p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="project_id (e.g. PRJ-001)"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value.toUpperCase())}
        />
        <button type="button" onClick={() => void load()} disabled={!projectId}>
          Load
        </button>
      </div>

      {project && (
        <>
          <h2>{project.project_id} · {project.name}</h2>
          <p>
            Client {project.client_id} · status <strong>{project.status}</strong> · pm_tool{' '}
            {project.pm_tool || '—'}
          </p>

          <fieldset disabled={!canEdit || busy} style={fs}>
            <legend>Add a delivery repo</legend>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                placeholder="repo URL"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="button" onClick={() => void addRepo()} disabled={!repoUrl.trim()}>
                Add repo
              </button>
            </div>
          </fieldset>

          <fieldset disabled={!canEdit || busy} style={fs}>
            <legend>Status</legend>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => void setStatus('active')}>
                Mark active
              </button>
              <button type="button" onClick={() => void setStatus('paused')}>
                Mark paused
              </button>
              <button type="button" onClick={() => void setStatus('closed')}>
                Mark closed
              </button>
            </div>
          </fieldset>

          <ChannelMapper projectId={project.project_id} canEdit={canEdit} />

          {note && <p style={{ color: 'green' }}>{note}</p>}
          <AuditTrail collection="projects" documentId={project.project_id} refreshKey={refreshKey} />
        </>
      )}

      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
    </section>
  );
}

const banner: React.CSSProperties = { background: '#fff7e6', border: '1px solid #ffd591', padding: 8, borderRadius: 4 };
const fs: React.CSSProperties = { border: '1px solid #ddd', padding: 12, marginTop: 12 };
